/**
 * L3: the qi pool bar (ลมปราณสะสม) under the cultivation line, and the "ทะลวงขั้น" button (HUD + วันนี้ panel).
 * Reads state only; the button sends `breakthroughUse` (systems/breakthrough.ts). Self-installing, polls at 2 Hz.
 */
import { GOODS, RECIPES, QI_POOL, type PlayerCommand, type World } from '@nine/shared';
import { $, h } from './dom';
import './qiPool.css';

interface Gate {
  held: boolean;
  pill: string | null;
  open: boolean;
  cool: number;
}
const gateOf = (w: World): Gate => {
  const b = w.sys.breakthrough;
  const st = w.progress.breakthrough;
  return { held: !!b?.held(), pill: b?.pill() ?? null, open: !!st?.open, cool: st?.cool ?? 0 };
};

/** The breakthrough control: a button, plus a line of what is missing / the retry countdown. Null when not held. */
export function breakthroughControl(w: World, send?: (c: PlayerCommand) => void): HTMLElement | null {
  const g = gateOf(w);
  if (!g.held) return null;
  const have = g.pill ? (w.progress.trades.goods[g.pill] ?? 0) : 0;
  const btn = h('button', { type: 'button', class: 'bt-btn' }, 'ทะลวงขั้น') as HTMLButtonElement;
  const notes: string[] = [];
  if (g.open && g.cool > 0) notes.push(`พ่ายแพ้ — ลองใหม่ได้อีก ${Math.ceil(g.cool / 60)} นาที`);
  else if (g.open) notes.push('เปิดด่านแล้ว — ท้าผู้พิทักษ์ที่ลานประลอง');
  else if (have < 1) {
    const r = g.pill ? RECIPES[g.pill] : undefined;
    if (r) notes.push(`ต้องมี${GOODS[g.pill!]?.name ?? 'ยาทะลวงขั้น'}: ${r.inputs.map((i) => `${GOODS[i.id]?.name ?? i.id} x${i.n}`).join(', ')} (หมอยา)`);
  }
  btn.disabled = g.open || have < 1;
  btn.addEventListener('click', () => send?.({ t: 'breakthroughUse' } as PlayerCommand));
  return h('div', { class: 'bt-wrap' }, btn, ...notes.map((n) => h('div', { class: 'bt-note' }, n)));
}

export function installQiPool(w: World, send: (c: PlayerCommand) => void): void {
  const host = document.querySelector('.pf-meta')?.parentElement;
  if (!host) return;
  const fill = h('i');
  const hint = h('div', { class: 'qp-hint' });
  const txt = h('div', {});
  const root = h('div', { class: 'qp', hidden: true }, txt, h('div', { class: 'qp-bar' }, fill), hint);
  const bt = h('div', { class: 'bt-slot' });
  root.addEventListener('pointerdown', (e) => e.stopPropagation());
  $('.pf-meta').after(root, bt);
  let key = '';
  setInterval(() => {
    const q = w.progress.qiPool ?? 0;
    root.hidden = q <= 0;
    if (q > 0) {
      const f = Math.min(1, q / QI_POOL.cap);
      txt.textContent = `ลมปราณสะสม ${Math.floor(q).toLocaleString('th-TH')} / ${QI_POOL.cap.toLocaleString('th-TH')}`;
      fill.style.width = `${(f * 100).toFixed(1)}%`;
      hint.textContent = w.player.meditating ? '' : 'นั่งสมาธิเพื่อย่อยลมปราณ';
    }
    const g = gateOf(w);
    const k = `${g.held}|${g.open}|${Math.ceil(g.cool / 60)}|${g.pill ? (w.progress.trades.goods[g.pill] ?? 0) : 0}`;
    if (k === key) return;
    key = k;
    const c = breakthroughControl(w, send);
    bt.replaceChildren(...(c ? [c] : []));
  }, 500);
}
